import Layout from "../Layout";
import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
import axios from 'axios'; // Import axios
import { LockKeyhole } from 'lucide-react';

// --- IMPORTANT: Store this key securely, do NOT hardcode in production ---
const SECRET_COOKIE_KEY = "mySuperSecretCookieKey123!@#"; // Must match Register.js

// Helper functions for encryption/decryption
const encrypt = (text) => {
  return CryptoJS.AES.encrypt(text, SECRET_COOKIE_KEY).toString();
};

const decrypt = (ciphertext) => {
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_COOKIE_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (e) {
    console.error("Error decrypting cookie:", e);
    return null;
  }
};

const API_BASE_URL = 'https://stage.beachcabana.lk/hotelmanage/api.php';
const AUTH_HEADER = {
  headers: {
    'Authorization': 'Basic YWRtaW46cGFzc3dvcmQ=' // admin:password encoded to Base64
  }
};

export default function Menu() {
  const sectionRefs = useRef({});
  const categoryBarRef = useRef(null); // Ref for the category bar container
  const navigate = useNavigate();

  const [activeCategory, setActiveCategory] = useState(null);
  const [cart, setCart] = useState(() => {
    try {
      // Attempt to retrieve and decrypt the cart from localStorage
      const encryptedCart = localStorage.getItem("cart");
      if (encryptedCart) {
        const decryptedCart = decrypt(encryptedCart);
        if (decryptedCart) {
          return JSON.parse(decryptedCart);
        }
      }
    } catch (e) {
      console.error("Failed to decrypt or parse cart from localStorage:", e);
    }
    return {}; // Return empty object if decryption or parsing fails
  });
  const [showTokenPopup, setShowTokenPopup] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [tokenError, setTokenError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [categories, setCategories] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [isFetchingCategories, setIsFetchingCategories] = useState(true);


    // Create an array of refs for the 5 input fields
    const inputRefs = useRef([]);
    // Initialize the refs array with nulls, it will be populated when inputs render
    if (inputRefs.current.length !== 5) {
      inputRefs.current = Array(5).fill(null).map((_, i) => inputRefs.current[i] || React.createRef());
    }


      // Handler for individual input changes
  const handleChange = (e, index) => {
    const { value } = e.target;
    if (value.length > 1 || (value && !/^[a-zA-Z0-9]$/.test(value))) {
      return;
    }

    // Initialize token array with existing characters or empty strings
    const newTokenArray = Array(5).fill('');
    for (let i = 0; i < 5 && i < tokenInput.length; i++) {
      newTokenArray[i] = tokenInput[i] || '';
    }
    // Update the current index with the new value
    newTokenArray[index] = value.toUpperCase();
    const updatedToken = newTokenArray.join('');

    console.log(`handleChange: index=${index}, value=${value}, updatedToken=${updatedToken}, tokenInput=${tokenInput}`); // Debug log

    // Update state
    setTokenInput(updatedToken);
    setTokenError(""); // Clear error on input change

    // Move focus to the next input
    if (value && index < 4) {
      inputRefs.current[index + 1]?.focus();
    }

    // Trigger automatic submission if token is exactly 5 characters
    if (updatedToken.length === 5) {
      console.log("Triggering automatic submission for token:", updatedToken);
      handleTokenSubmit(null, updatedToken); // Pass updatedToken directly
    }
  };
  

  // Fetch categories from API on mount
  useEffect(() => {
    const fetchCategories = async () => {
      setIsFetchingCategories(true);
      setFetchError(null);
      try {
        const response = await axios.get(`${API_BASE_URL}?param=getmenu`, AUTH_HEADER); //
        if (response.data.code === 200) { //
          const fetchedCategories = response.data.data; //
          // Sort categories by id to maintain consistent order
          fetchedCategories.sort((a, b) => a.id - b.id);
          setCategories(fetchedCategories);
          if (fetchedCategories.length > 0) {
            setActiveCategory(fetchedCategories[0].id);
          }
        } else {
          setFetchError("Failed to load menu categories. Please try again later."); //
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
        setFetchError("Failed to load menu categories. Please try again later.");
      } finally {
        setIsFetchingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Encrypt and save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      const cartString = JSON.stringify(cart);
      const encryptedCart = encrypt(cartString);
      localStorage.setItem("cart", encryptedCart);
    } catch (e) {
      console.error("Failed to encrypt or save cart to localStorage:", e);
    }
  }, [cart]);

  // Check device registration and token on mount
  useEffect(() => {
    const checkDeviceRegistration = () => {
      const encryptedDeviceRegistered = Cookies.get('deviceRegistered');
      const encryptedregistrationToken = Cookies.get('registrationToken');
      const encryptedRegistrationExpiry = Cookies.get('registrationExpiry');

      if (encryptedDeviceRegistered && encryptedregistrationToken && encryptedRegistrationExpiry) {
        const deviceRegistered = decrypt(encryptedDeviceRegistered);
        const registrationToken = decrypt(encryptedregistrationToken);
        const registrationExpiry = decrypt(encryptedRegistrationExpiry);

        if (deviceRegistered === 'true' && registrationToken && registrationExpiry) {
          const expiryDate = new Date(registrationExpiry);
          if (new Date() < expiryDate) {
            setIsRegistered(true);
            console.log("Device is registered with valid token.");
          } else {
            // Registration expired, clear cookies
            Cookies.remove('deviceRegistered');
            Cookies.remove('registrationToken');
            Cookies.remove('registrationExpiry');
            Cookies.remove('currentRegistrationId');
            setIsRegistered(false);
            setShowTokenPopup(true);
            console.log("Registration expired. Prompting for token.");
          }
        } else {
          // Invalid or corrupted cookies
          Cookies.remove('deviceRegistered');
          Cookies.remove('registrationToken');
          Cookies.remove('registrationExpiry');
          Cookies.remove('currentRegistrationId');
          setIsRegistered(false);
          setShowTokenPopup(true);
          console.log("Invalid cookies. Prompting for token.");
        }
      } else {
        // Cookies missing, show token popup
        setIsRegistered(false);
        setShowTokenPopup(true);
        console.log("No registration cookies found. Prompting for token.");
      }
    };

    checkDeviceRegistration();
  }, []);


  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      const newTokenArray = Array(5).fill('');
      for (let i = 0; i < 5 && i < tokenInput.length; i++) {
        newTokenArray[i] = tokenInput[i] || '';
      }
      if (tokenInput[index]) {
        newTokenArray[index] = '';
        setTokenInput(newTokenArray.join(''));
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
      setTokenError(""); // Clear error on backspace
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 4) {
      inputRefs.current[index + 1]?.focus();
    }
  };


  const handleTokenSubmit = async (e, token = tokenInput) => {
    // Prevent default form submission if triggered by a form event
    if (e) {
      e.preventDefault();
    }

    setIsLoading(true);
    setTokenError("");

    // Use the provided token (from handleChange) or fallback to tokenInput
    if (token.length !== 5) {
      console.log("Submission failed: token=", token, "tokenInput=", tokenInput); // Debug log
      setTokenError("Please enter all 5 characters of the token.");
      setIsLoading(false);
      return;
    }

    console.log("Submitting token:", token); // Debug log

    try {
      const response = await axios.post(`${API_BASE_URL}?param=tokenvalidate`, { token }, AUTH_HEADER);

      if (response.data.code === 200 && response.data.data) {
        const registrationData = response.data.data;
        const expiryDate = new Date(registrationData.registrationExpiresAt);

        if (new Date() < expiryDate) {
          Cookies.set('deviceRegistered', encrypt('true'), { expires: expiryDate });
          Cookies.set('registrationToken', encrypt(token), { expires: expiryDate });
          Cookies.set('registrationExpiry', encrypt(expiryDate.toISOString()), { expires: expiryDate });
          Cookies.set('currentRegistrationId', encrypt(registrationData.registrationToken), { expires: expiryDate });

          setIsRegistered(true);
          setShowTokenPopup(false);          
          setTokenInput(""); // Clear token input after successful validation
          console.log("Token verified successfully. Cookies set.");
        } else {
          setTokenError("This token has expired. Please register again or use a valid token.");
        }
      } else {
        setTokenError(response.data.message || "Invalid token. Please check and try again.");
      }
    } catch (err) {
      console.error("Error verifying token:", err);
      setTokenError("Error verifying token. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToCategory = (id) => {
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveCategory(id);
    }
  };

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-20% 0px -80% 0px",
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const intersectingCategory = categories.find(cat => sectionRefs.current[cat.id] === entry.target);
          if (intersectingCategory) {
            setActiveCategory(intersectingCategory.id);
          }
        }
      });
    }, observerOptions);

    const currentSectionRefs = sectionRefs.current;

    categories.forEach((cat) => {
      if (currentSectionRefs[cat.id]) {
        observer.observe(currentSectionRefs[cat.id]);
      }
    });

    return () => {
      categories.forEach((cat) => {
        if (currentSectionRefs[cat.id]) {
          observer.unobserve(currentSectionRefs[cat.id]);
        }
      });
    };
  }, [categories]);

  // Effect to scroll the category bar horizontally when activeCategory changes
  useEffect(() => {
    if (activeCategory && categoryBarRef.current) {
      const activeCategoryButton = categoryBarRef.current.querySelector(
        `button[data-category-id="${activeCategory}"]`
      );
      if (activeCategoryButton) {
        const container = categoryBarRef.current;
        const buttonRect = activeCategoryButton.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Calculate scroll position to center the button
        const scrollLeft =
          buttonRect.left +
          container.scrollLeft -
          containerRect.left -
          containerRect.width / 2 +
          buttonRect.width / 2;

        container.scrollTo({
          left: scrollLeft,
          behavior: "smooth",
        });
      }
    }
  }, [activeCategory, categories]); // Re-run when categories change to ensure buttons are rendered


  // Modified addToCart to include categoryId
 const addToCart = (item, categoryId) => { // Accepts categoryId
      setCart((prev) => {
        const currentQuantityInCart = prev[item.id]?.quantity || 0;
        if (item.quantity > 0 && currentQuantityInCart < item.quantity) {
          return {
            ...prev,
            [item.id]: { ...item, maxqty:item.quantity, quantity: currentQuantityInCart + 1, categoryId: categoryId }, // Saves categoryId
          };
        }
        return prev;
      });
    };

  const removeFromCart = (item) => {
    setCart((prev) => {
      const newCart = { ...prev };
      if (newCart[item.id]) {
        newCart[item.id].quantity -= 1;
        if (newCart[item.id].quantity <= 0) delete newCart[item.id];
      }
      return newCart;
    });
  };

  const getItemQuantity = (itemId) => cart[itemId]?.quantity || 0;

  const cartCount = Object.values(cart).reduce((acc, item) => acc + item.quantity, 0);

  const handleViewOrderClick = () => {
    navigate("/vieworder");
  };

  return (
    <Layout pageTitle="Menu" backTo="/">
      <div className={`${isRegistered ? '' : 'opacity-50 pointer-events-none'}`}>
        {isFetchingCategories && (
          <div className="text-center py-8">
            <p className="text-lg text-gray-600">Loading menu...</p>
          </div>
        )}
        {fetchError && (
          <div className="text-center py-8">
            <p className="text-lg text-red-500">{fetchError}</p>
          </div>
        )}
        {!isFetchingCategories && !fetchError && categories.length === 0 && (
          <div className="text-center py-8">
            <p className="text-lg text-gray-600">No menu categories available.</p>
          </div>
        )}
        {!isFetchingCategories && !fetchError && categories.length > 0 && (
          <>
            {/* Category scroll buttons */}
            <div ref={categoryBarRef} className="sticky top-0 z-10 bg-white py-2 overflow-x-auto scrollbar-hide">
              <div className="flex gap-2 px-2">
                {categories
                  .filter((cat) => cat.visible)
                  .map((cat) => (
                    <button
                      key={cat.id}
                      data-category-id={cat.id} // Add data attribute to easily find the button
                      className={`whitespace-nowrap text-sm px-4 py-2 rounded-full transition ${
                        activeCategory === cat.id
                          ? "bg-black text-white"
                          : "bg-gray-100 hover:bg-gray-200"
                      }`}
                      onClick={() => scrollToCategory(cat.id)}
                    >
                      {cat.name}
                    </button>
                  ))}
              </div>
            </div>

            {/* Categories and items */}
            <div className="mt-4 pb-28">
              {categories
                .filter((cat) => cat.visible)
                .map((cat) => (
                  <div key={cat.id} ref={(el) => (sectionRefs.current[cat.id] = el)} className="mb-8">
                    <h2 className="text-xl font-semibold mb-2">{cat.name}</h2>
                    {cat.note && <p className="text-sm text-red-500 mb-3">{cat.note}</p>}
                    {cat.items.map((item) => {
                      const currentQuantityInCart = getItemQuantity(item.id);
                      const isOutOfStock = item.quantity === 0;
                      const isMaxQuantityReached = currentQuantityInCart >= item.quantity;

                      return (
                        <div key={item.id} className="flex items-center border-b py-3">
                          <img
                            src={`/images/menu/${item.image}`}
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                          <div className="flex-1 ml-4">
                            <h4 className="text-md font-medium">{item.name}</h4>
                            <p className="text-sm text-gray-500">LKR {item.price}.00</p>
                            {isOutOfStock && (
                              <p className="text-xs text-red-500 font-semibold mt-1">Out of Stock</p>
                            )}
                          </div>
                          <div className="flex items-center bg-gray-100 rounded-full px-5 py-2 gap-3 text-lg">
  <button
    className={`w-8 h-8 rounded-full shadow text-center leading-8 text-xl ${
      currentQuantityInCart > 0 ? "bg-white" : "bg-gray-200 cursor-not-allowed"
    }`}
    onClick={() => removeFromCart(item)}
    disabled={currentQuantityInCart === 0}
  >
    -
  </button>
  <span className="min-w-[1.5rem] text-center">{currentQuantityInCart}</span>
  <button
    className={`w-8 h-8 rounded-full shadow text-center leading-8 text-xl ${
      isOutOfStock || isMaxQuantityReached
        ? "bg-gray-200 cursor-not-allowed"
        : "bg-white"
    }`}
    onClick={() => addToCart(item, cat.id)}
    disabled={isOutOfStock || isMaxQuantityReached}
  >
    +
  </button>
</div>
                        </div>
                      );
                    })}
                  </div>
                ))}
            </div>

            {/* Cart count and View order */}
            {cartCount > 0 && (
              <div
                className="fixed bottom-4 left-4 right-4 bg-black text-white px-6 py-3 rounded-full flex justify-between items-center shadow-md cursor-pointer"
                onClick={handleViewOrderClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleViewOrderClick();
                }}
                aria-label="View order cart"
              >
                <div className="p-1">
                  <div className="text-sm font-semibold">View order</div>
                </div>
                <span className="bg-white text-black w-6 h-6 flex items-center justify-center rounded-full text-sm font-medium">
                  {cartCount}
                </span>
              </div>
            )}
          </>
        )}
      </div>

       {/* Token Verification Popup */}
       {showTokenPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
          <div className="bg-white w-full max-w-sm mx-auto rounded-lg shadow-lg p-6 text-center">
            {/* Centered LockKeyhole icon */}
            <div className="flex justify-center mb-4">
              <LockKeyhole className="w-8 h-8 text-blue-600" />
            </div>

            <h3 className="text-xl font-bold mb-4 text-gray-800">Enter Registration Token</h3>
            <p className="text-gray-700 mb-6">
              This is the first 5 characters of your Passport number or national ID number provided during registration.
            </p>
            <form onSubmit={handleTokenSubmit}>
              <div className="flex justify-center gap-2 mb-4">
                {[...Array(5)].map((_, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength={1}
                    value={tokenInput[index] || ''}
                    onChange={(e) => handleChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    ref={(el) => (inputRefs.current[index] = el)}
                    className="w-10 h-10 text-center text-lg font-semibold border rounded-lg border-gray-300 p-0"
                    autoFocus={index === 0}
                  />
                ))}
              </div>
              {tokenError && (
                <p className="text-sm text-red-500 mb-4">{tokenError}</p>
              )}

              <div className="text-center">
                <button
                  type="submit"
                  className={`px-6 py-2 w-full bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 ${
                    isLoading || tokenInput.length !== 5 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={isLoading || tokenInput.length !== 5}
                >
                  {isLoading ? "Verifying..." : "Submit"}
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="px-6 py-2 w-full bg-white text-gray-900 font-medium text-sm rounded-lg hover:bg-gray-100 mt-3"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}